import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, BookX, FileQuestion, Activity, RefreshCw,
  ChevronRight, Zap,
} from 'lucide-react';
import {
  statistiquesService,
  NotesDashboardAlertesResponse,
} from '../../services/api.service';

interface Props {
  anneeAcademique?: string;
  pole?: string;
}

const AlertesNotesSection: React.FC<Props> = ({ anneeAcademique, pole }) => {
  const [data, setData] = useState<NotesDashboardAlertesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [err, setErr] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await statistiquesService.getNotesDashboardAlertes({
        annee_academique: anneeAcademique,
        semestre,
        pole,
        limit: 10,
      });
      setData(res);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anneeAcademique, pole, semestre]);

  if (err) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-300">
        Erreur : {err}
      </div>
    );
  }

  const s = data?.stats;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <AlertTriangle className="text-amber-600 dark:text-amber-400" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Alertes sur les Notes
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Matières et classes nécessitant une action
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={semestre}
            onChange={e => setSemestre(e.target.value as 'S1' | 'S2')}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
          >
            <option value="S1">Semestre 1</option>
            <option value="S2">Semestre 2</option>
          </select>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
            title="Actualiser"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Stats cards */}
      {s && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-5 border-b border-gray-100 dark:border-gray-700">
          <AlertCard
            icon={<BookX size={18} />}
            color="red"
            title="Matières sans devoirs"
            count={s.nb_matieres_sans_devoirs}
            total={s.total_matieres}
            pct={s.pct_matieres_sans_devoirs}
          />
          <AlertCard
            icon={<FileQuestion size={18} />}
            color="orange"
            title="Matières sans examens"
            count={s.nb_matieres_sans_examens}
            total={s.total_matieres}
            pct={s.pct_matieres_sans_examens}
          />
          <AlertCard
            icon={<Activity size={18} />}
            color="purple"
            title="Classes complétude <50%"
            count={s.nb_classes_faibles}
            total={null}
            pct={null}
          />
        </div>
      )}

      {/* Listes détaillées */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100 dark:divide-gray-700">
          <DetailsList
            title="Sans notes de devoirs"
            items={data.matieres_sans_devoirs.map(m => ({
              primary: m.matiere_nom,
              secondary: `${m.matiere_code} · ${m.niveau} · ${m.classes.join(', ')}`,
              value: `${m.nb_etudiants_attendus} étud.`,
            }))}
            emptyMsg="Toutes les matières ont au moins une note de devoir ✓"
            actionLabel="Saisir / Reporter"
            actionLink="/saisie-notes-devoirs"
            colorClass="text-red-600 dark:text-red-400"
          />
          <DetailsList
            title="Sans notes d'examens"
            items={data.matieres_sans_examens.map(m => ({
              primary: m.matiere_nom,
              secondary: `${m.matiere_code} · ${m.niveau} · ${m.classes.join(', ')}`,
              value: `${m.nb_etudiants_attendus} étud.`,
            }))}
            emptyMsg="Toutes les matières ont au moins une note d'examen ✓"
            actionLabel="Saisir / Attribuer 0"
            actionLink="/saisie-notes-examens"
            colorClass="text-orange-600 dark:text-orange-400"
          />
          <DetailsList
            title="Classes à compléter"
            items={data.classes_completude_faible.map(c => ({
              primary: c.classe,
              secondary: `${c.niveau} · ${c.option} · ${c.nb_matieres} matières`,
              value: `${c.pct_global}%`,
            }))}
            emptyMsg="Toutes les classes ont >50% de complétude ✓"
            actionLabel="Détails"
            actionLink="/saisie-notes-devoirs"
            colorClass="text-purple-600 dark:text-purple-400"
          />
        </div>
      )}
    </div>
  );
};

// ─── Carte de stat alerte ──────────────────────────────────────────────────
const AlertCard: React.FC<{
  icon: React.ReactNode;
  color: 'red' | 'orange' | 'purple';
  title: string;
  count: number;
  total: number | null;
  pct: number | null;
}> = ({ icon, color, title, count, total, pct }) => {
  const colors = {
    red: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
    orange: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800',
    purple: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800',
  }[color];
  return (
    <div className={`p-3 border rounded-lg ${colors}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="text-xs font-medium">{title}</div>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold">{count}</span>
        {total !== null && (
          <span className="text-xs opacity-70 mb-1">/ {total}</span>
        )}
        {pct !== null && (
          <span className="text-xs opacity-70 mb-1 ml-auto">{pct}%</span>
        )}
      </div>
    </div>
  );
};

// ─── Liste détaillée ────────────────────────────────────────────────────────
const DetailsList: React.FC<{
  title: string;
  items: Array<{ primary: string; secondary: string; value: string }>;
  emptyMsg: string;
  actionLabel: string;
  actionLink: string;
  colorClass: string;
}> = ({ title, items, emptyMsg, actionLabel, actionLink, colorClass }) => (
  <div className="p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className={`text-sm font-semibold ${colorClass}`}>{title}</h3>
      <Link
        to={actionLink}
        className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
      >
        <Zap size={10} /> {actionLabel}
      </Link>
    </div>
    {items.length === 0 ? (
      <div className="text-xs text-green-600 dark:text-green-400 py-4 text-center">
        {emptyMsg}
      </div>
    ) : (
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {items.map((it, idx) => (
          <div
            key={idx}
            className="flex items-start gap-2 py-1.5 px-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded text-xs"
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {it.primary}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                {it.secondary}
              </div>
            </div>
            <span className="text-[10px] font-mono font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap mt-0.5">
              {it.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default AlertesNotesSection;
